<template>
    <section class="mb-48">
        <application-title :title="$t('interview.interviews')">
            <div class="flex items-center gap-8">
                <application-button
                    :condition="canManage"
                    :title="$t('interview.add_interview')"
                    icon="calendar-add-on"
                    @click="createInterview"
                />
                <application-button
                    :condition="canManage"
                    :title="$t('interview.direct_interview')"
                    icon="forum"
                    @click="createDirectInterview"
                />
            </div>
        </application-title>

        <loading v-if="fetchingInterviews"></loading>
        <ul v-else-if="interviews.length" class="pt-32">
            <li v-for="interview in interviews"
                :key="'interview-' + interview.id"
                class="odd:bg-primary-5 relative flex items-center"
            >
                <div v-show="editInterviewLabel === interview.id" class="p-16 mr-48 flex-grow break-words">
                    <click-edit-input
                        :ref="'edit-' + interview.id"
                        :disabled="!canManage"
                        :value="interview.name ? interview.name : moment(interview.starts_at).locale(locale).format(dateFormat)"
                        class="mr-auto font-semibold group-hover:text-primary trans-ease"
                        @input="editInterviewLabel = null; editInterviewTitle($event, interview.id)"
                    />
                </div>

                <button v-show="editInterviewLabel !== interview.id"
                        class="group flex flex-grow items-center p-16 w-full"
                        role="button"
                        @click.prevent="editInterview(interview)"
                >
                    <span class="text-left flex items-center gap-4 mr-auto group-hover:text-primary trans-ease break-words">
                        <tooltip
                            v-if="interview.status"
                            wrapperClass="inline-block"
                            :content="interview.status.name"
                            position="right"
                        >
                            <svg-icon class="w-18 h-18" :class="'fill-' + interview.status.color" :type="interview.status.icon" />
                        </tooltip>
                        <span class="font-semibold" v-if="interview.name">
                            {{ interview.name }}
                        </span>
                        <span class="inline-block">
                            <span class="italic" v-if="interview.status.id === 5">
                                {{ $t('interview.status.rescheduled_by_interviewee') }}
                            </span>
                            <span class="italic" v-else-if="interview.status.id === 6">
                                {{ $t('interview.status.cancelled_by_interviewer') }}
                            </span>
                            <span class="italic" v-else-if="interview.status.id === 7">
                                {{ $t('interview.status.cancelled_by_interviewee') }}
                            </span>
                            <span class="italic" v-else-if="!interview.starts_at">
                                {{ $t('interview.send_on') }}
                                {{ interviewDateTime(!!interview.starts_at ? interview.starts_at : interview.created_at) }}
                            </span>
                        </span>
                    </span>

                    <div class="pl-8 flex">
                        <div class="flex pl-8 mr-16">
                            <tooltip
                                v-for="sub in interview.subscriptions.filter(sub => sub.required && sub.user)"
                                :key="sub.user.id"
                                :content="sub.user.name"
                                class="-mr-8 w-32 h-32"
                                position="bottom"
                            >
                                <avatar
                                    :image="sub.user.image_url"
                                    size="small"
                                    class="cursor-default border border-white"
                                >{{ sub.user.initials }}
                                </avatar>
                            </tooltip>
                        </div>
                    </div>
                </button>

                <div v-if="canManage" class="mr-16 flex items-center space-x-8">
                    <div v-if="interview.visibility === 'me'" class="flex items-center h-32 w-20" :title="$t('wysiwyg.visibilityOptions.me')">
                        <svg-icon class="max-w-16 max-h-16 text-primary-20" type="lock"></svg-icon>
                    </div>

                    <simple-dropdown
                        class="flex items-center"
                        position="right"
                        @closed="(visibleForInfo === interview.id ? visibleForInfo = null : '' )"
                        @opened="visibleForInfo = interview.id"
                        without-chevron
                    >
                        <template #trigger>
                            <svg-icon class="w-18 h-18 text-primary-80 hover:text-primary" type="visibility"></svg-icon>
                        </template>
                        <template #content>
                            <div class="bg-white rounded-6 w-360 overflow-hidden -space-y-px">
                                <div v-for="(option, i) in visibilityOptions(interview)" :key="option.key"
                                     :class="[option.key === interview.visibility ? 'bg-secondary-5 border-secondary z-10' : 'border-primary-20', i === 0 ? 'rounded-t' : '', i === visibilityOptions(interview).length - 1 ? 'rounded-b' : '']"
                                     class="relative border p-16 flex cursor-pointer"
                                     @click="option.fn(interview.id)">
                                    <radio-button
                                        v-model="interview.visibility"
                                        :value="option.key"
                                    />
                                    <div class="ml-8 flex flex-col cursor-pointer">
                                        <span
                                            :class="option.key === interview.visibility ? 'text-secondary' : 'text-primary-80'"
                                            class="block leading-5"
                                        >
                                            {{ option.title }}
                                        </span>
                                        <span
                                            v-if="option.key !== 'selected'"
                                            :class="option.key === interview.visibility ? 'text-secondary-80' : 'text-primary-80'"
                                            class="block text-body-sm leading-5 mt-2"
                                        >
                                            {{ option.description }}
                                        </span>
                                        <div v-else class="mt-8 w-240">
                                            <user-avatars size="small" :users="getUserModels(interview.users)" left/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </simple-dropdown>

                    <button
                        class="text-primary-80 hover:text-semantic-error cursor-pointer trans-ease pointer-events-auto h-32 w-32 flex items-center justify-center rounded-6"
                        @click="confirmDelete(interview)">
                        <svg-icon class="max-w-20 max-h-20" type="delete"></svg-icon>
                    </button>
                </div>
            </li>
        </ul>

        <interview v-if="showInterview"
                   :application="application"
                   :can-manage="canManage"
                   :interview="editingInterview"
                   @update="editInterview($event)"
                   @close="stopEdit"
        />

        <popup-modal-confirm
            v-if="canManage && deleting"
            :confirmText="$t('interview.delete')"
            :message="$t('interview.confirm_delete')"
            show-exclamation
            :working="performingDeleteRequest"
            @close="cancelDelete"
            @confirm="deleteInterview(deleting)"
        />

        <interview-cancel
            v-if="cancelingInterview"
            v-model="cancellationMail"
            :loading="performingDeleteRequest"
            :send-mail="sendCancellationMail"
            @changeSendMail="sendCancellationMail = $event"
            @close="cancelingInterview = false"
            @confirm="cancelInterview($event)"
        />

        <popup-modal v-if="showUserSelectionFor" @close="showUserSelectionFor = false">
            <p class="font-semibold text-body mb-16 text-center">
                {{ $t('wysiwyg.user_selection_title') }}
            </p>
            <div class="-space-y-1 border border-primary-20 rounded">
                <checkbox
                    v-for="(user, i) in users"
                    v-model="selectedUserIds"
                    :key="user.id"
                    class="p-8 odd:bg-primary-5 even:bg-white flex items-center first-of-type:rounded-t last-of-type:rounded-b"
                    label-class="flex-1 justify-between"
                    :value="user.id"
                    slot-before
                >
                    <div class="flex items-center">
                        <avatar :image="user.image_url" size="small">
                            {{ user.initials }}
                        </avatar>
                        <p class="ml-8">{{ user.name }}</p>
                    </div>
                </checkbox>
            </div>

            <simple-button class="mt-16 ml-auto" display="primary" @click="saveSelectedUsers">
                {{ $t('wysiwyg.user_selection_button') }}
            </simple-button>
        </popup-modal>
    </section>
</template>

<script>
import Loading from './ui/Loading.vue';
import SimpleButton from './ui/SimpleButton.vue';
import * as interviews from '../services/InterviewService';
import Interview from '../models/Interview';
import {normalizeJson} from '../../shared/helpers';
import SvgIcon from './ui/SvgIcon.vue';
import PopupModalConfirm from './ui/PopupModalConfirm.vue';
import moment from 'moment';
import InterviewComponent from './Interview.vue';
import Tooltip from './navigation/Tooltip.vue';
import UserAvatars from './ui/UserAvatars.vue';
import Avatar from './ui/Avatar.vue';
import ClickEditInput from './forms/ClickEditInput.vue';
import SimpleDropdown from './ui/SimpleDropdown.vue';
import PopupModal from './ui/PopupModal';
import Checkbox from './forms/Checkbox';
import User from '../models/User';
import InterviewCancel from "./InterviewCancel";
import ApplicationButton from "./ui/ApplicationButton.vue";
import ApplicationTitle from "./applications/ApplicationTitle.vue";
import RadioButton from "./forms/RadioButton.vue";
import {mapStores} from "pinia";
import {useRepo} from "pinia-orm";
import {useUsersStore} from "../state/modules/users";
import {useMainStore} from "../state/store";

export default {
    components: {
        RadioButton,
        ApplicationTitle,
        ApplicationButton,
        Checkbox,
        PopupModal,
        SimpleDropdown,
        ClickEditInput,
        UserAvatars,
        Avatar,
        Tooltip,
        InterviewCancel,
        'loading': Loading,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'interview': InterviewComponent,
    },
    props: {
        application: Object,
        canManage: Boolean,
    },
    data: function () {
        return {
            moment,
            fetchingInterviews: true,
            deleting: null,
            cancelingInterview: false,
            cancellationMail: {
                attachments: [],
                subject: '',
                message: ''
            },
            sendCancellationMail: true,
            performingDeleteRequest: false,
            creating: false,
            showInterview: false,
            editingInterview: null,
            editInterviewLabel: null,

            selectedUserIds: [],
            showUserSelectionFor: null,
            visibleForInfo: null,
        };
    },
    computed: {
        ...mapStores(useMainStore, useUsersStore),
        locale() {
            return this.mainStore.locale;
        },
        dateFormat() {
            return this.mainStore.dateFormat;
        },
        currentUserId() {
            return this.mainStore.currentUser.id;
        },
        users() {
            return this.usersModuleStore.allUsersExceptMe;
        },
        interviews() {
            return useRepo(Interview)
                .with('user')
                .with('interview_answers')
                .with('subscriptions', (q) => {
                    q.with('user')
                })
                .with('invitation')
                .with('timeslots')
                .where('application_id', this.application.id)
                .orderBy('starts_at')
                .get();
        },
    },
    methods: {
        fetchInterviews() {
            this.fetchingInterviews = true;
            interviews.index(this.application.id).then(results => {
                return useRepo(Interview).save(normalizeJson(results));
            }).then(() => {
                this.fetchingInterviews = false;
            });
        },
        confirmDelete(interview) {
            if (!interview.status || ![6, 7].includes(interview.status.id) && !interview.instant) {
                this.cancelingInterview = interview;
            } else {
                this.deleting = interview;
            }
        },
        cancelInterview(signature) {
            this.performingDeleteRequest = true;

            // add signature if present
            if (signature) {
                this.cancellationMail.message += signature;
            }

            interviews.cancel(this.cancelingInterview.id, {
                data: {
                    attributes: {
                        subject: this.cancellationMail.subject,
                        message: this.cancellationMail.message,
                    },
                    options: {
                        send_mail: this.sendCancellationMail,
                    }
                },
            }).then(result => {
                return useRepo(Interview)
                    .whereId(result.data.id)
                    .update(normalizeJson(result.data));
            }).then(() => {
                this.cancellationMail.subject = '';
                this.cancellationMail.message = '';
                this.cancelingInterview = false;
                this.performingDeleteRequest = false;
            });
        },
        cancelDelete() {
            this.deleting = null;
        },
        deleteInterview(interview) {
            this.performingDeleteRequest = true;

            interviews.destroy(interview.id).then(() => {
                useRepo(Interview).destroy(interview.id);
            }).then(() => {
                this.deleting = null;
                this.performingDeleteRequest = false;
            });
        },
        interviewDateTime(time) {
            return moment(time).locale(this.locale).format(this.dateFormat);
        },
        createDirectInterview() {
            this.editingInterview = new Interview;
            this.editingInterview.application_id = this.application.id;
            this.editingInterview.status = {
                id: 2 // Confirmed
            };
            this.editingInterview.instant = true;
            this.showInterview = true;
        },
        createInterview() {
            this.$emit('createInterview')
        },
        editInterview(interview) {
            this.showInterview = true;
            this.editingInterview = interview;
        },
        stopEdit() {
            this.showInterview = false;
            this.editingInterview = null;
        },
        editInterviewTitle(event, interviewId) {
            // Update the section in our store to change it immediately.
            useRepo(Interview)
                .whereId(interviewId)
                .update({name: event});

            // Update name in db
            interviews.update(interviewId, {data: {attributes: {name: event}}});
        },
        visibilityOptions(field) {
            return [
                {
                    key: 'all',
                    title: this.$t('wysiwyg.visibilityOptions.all'),
                    description: this.$t('wysiwyg.visibilityOptions.all_desc'),
                    fn: (fieldId) => this.updateField(fieldId, 'visibility', 'all'),
                },
                {
                    key: 'selected',
                    title: this.$t('wysiwyg.visibilityOptions.selected'),
                    description: this.$t('wysiwyg.visibilityOptions.selected_desc'),
                    fn: (fieldId) => {
                        this.showUserSelectionFor = fieldId;
                        this.selectedUserIds = this.selectedUsersForField(fieldId);
                        this.updateField(fieldId, 'visibility', 'selected');
                    },
                },
                ...field.user_id === this.currentUserId ? [{
                    key: 'me',
                    title: this.$t('wysiwyg.visibilityOptions.me'),
                    description: this.$t('wysiwyg.visibilityOptions.me_desc'),
                    fn: (fieldId) => this.updateField(fieldId, 'visibility', 'me'),
                }] : [],
            ];
        },
        updateField(fieldId, key, value) {
            let update = {
                [key]: value,
                visibility_only: true
            };
            if (key === 'visibility' && value === 'selected' && !this.selectedUserIds.includes(this.currentUserId)) {
                // If we're changing visibility, we have to make sure this user is part of the selected list.
                this.selectedUserIds.push(this.currentUserId);
                update['users'] = this.selectedUserIds;
            }

            useRepo(Interview)
                .whereId(fieldId)
                .update(update)

            interviews.update(fieldId, {
                data: {
                    attributes: update,
                },
            });
        },
        selectedUsersForField(fieldId) {
            if (!this.showUserSelectionFor) {
                return [];
            }

            return this.getField(fieldId)?.users || [];
        },
        getField(fieldId) {
            return this.interviews.find(i => i.id === fieldId);
        },
        getUserModels(userIds) {
            return useRepo(User).whereIn('id', userIds).get();
        },
        saveSelectedUsers() {
            this.updateField(this.showUserSelectionFor, 'users', this.selectedUserIds);

            this.showUserSelectionFor = false;
        },
    },
    mounted() {
        this.fetchInterviews();

        bus.addEventListener('open_interview', data => {
            this.showInterview = true;
            this.editingInterview = this.interviews.find(i => i.id === data.detail);
        });
    },
};
</script>
